import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { TreatmentPicker } from "@/components/treatment-picker";
import { trpc } from "@/trpc/trpc";
import { toast } from "sonner";

const DISCOUNT_TYPES = ["percentage", "fixed", "badge_only"] as const;
const BADGE_VARIANTS = [
  "default",
  "tonal",
  "success",
  "warning",
  "danger",
  "info",
] as const;

const schema = z.object({
  title: z.string().min(1, "Tytuł jest wymagany"),
  description: z.string().optional(),
  discountType: z.enum(DISCOUNT_TYPES),
  discountValue: z.coerce.number().min(0),
  badgeLabel: z.string().max(32).optional(),
  badgeVariant: z.enum(BADGE_VARIANTS),
  stackableWithCoupons: z.boolean(),
  validFrom: z.string().optional(),
  validUntil: z.string().optional(),
  isActive: z.boolean(),
  treatmentIds: z.array(z.string().uuid()).min(1, "Wybierz przynajmniej jeden zabieg"),
});

type FormData = z.infer<typeof schema>;

export type OfferForEdit = {
  id: string;
  title: string;
  description: string | null;
  discountType: "percentage" | "fixed" | "badge_only";
  discountValue: number;
  badgeLabel: string | null;
  badgeVariant: (typeof BADGE_VARIANTS)[number];
  stackableWithCoupons: boolean;
  validFrom: Date | string | null;
  validUntil: Date | string | null;
  isActive: boolean;
  treatments: { id: string; name: string }[];
};

export function OfferDialog({
  open,
  onOpenChange,
  offer,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  offer?: OfferForEdit | null;
}) {
  const utils = trpc.useUtils();
  const isEdit = !!offer;

  const createMutation = trpc.offer.create.useMutation({
    onSuccess: () => {
      utils.offer.list.invalidate();
      toast.success("Oferta dodana");
      onOpenChange(false);
    },
    onError: (err) => toast.error(err.message),
  });

  const updateMutation = trpc.offer.update.useMutation({
    onSuccess: () => {
      utils.offer.list.invalidate();
      toast.success("Oferta zaktualizowana");
      onOpenChange(false);
    },
    onError: (err) => toast.error(err.message),
  });

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: "",
      description: "",
      discountType: "percentage",
      discountValue: 10,
      badgeLabel: "",
      badgeVariant: "danger",
      stackableWithCoupons: true,
      validFrom: "",
      validUntil: "",
      isActive: true,
      treatmentIds: [],
    },
  });

  useEffect(() => {
    if (!open) return;
    if (offer) {
      const displayValue =
        offer.discountType === "fixed"
          ? offer.discountValue / 100
          : offer.discountValue;
      form.reset({
        title: offer.title,
        description: offer.description ?? "",
        discountType: offer.discountType,
        discountValue: displayValue,
        badgeLabel: offer.badgeLabel ?? "",
        badgeVariant: offer.badgeVariant,
        stackableWithCoupons: offer.stackableWithCoupons,
        validFrom: offer.validFrom
          ? new Date(offer.validFrom).toISOString().slice(0, 10)
          : "",
        validUntil: offer.validUntil
          ? new Date(offer.validUntil).toISOString().slice(0, 10)
          : "",
        isActive: offer.isActive,
        treatmentIds: offer.treatments.map((t) => t.id),
      });
    } else {
      form.reset({
        title: "",
        description: "",
        discountType: "percentage",
        discountValue: 10,
        badgeLabel: "",
        badgeVariant: "danger",
        stackableWithCoupons: true,
        validFrom: "",
        validUntil: "",
        isActive: true,
        treatmentIds: [],
      });
    }
  }, [open, offer, form]);

  const watchType = form.watch("discountType");
  const watchValue = form.watch("discountValue");
  const watchBadgeLabel = form.watch("badgeLabel");
  const watchBadgeVariant = form.watch("badgeVariant");
  const watchTreatmentIds = form.watch("treatmentIds");
  const watchStackable = form.watch("stackableWithCoupons");
  const watchActive = form.watch("isActive");

  const computedBadgePreview = useMemo(() => {
    if (watchBadgeLabel && watchBadgeLabel.length > 0) return watchBadgeLabel;
    if (watchType === "percentage" && watchValue > 0) return `-${watchValue}%`;
    if (watchType === "fixed" && watchValue > 0)
      return `-${Math.round(Number(watchValue))} zł`;
    return "PROMO";
  }, [watchBadgeLabel, watchType, watchValue]);

  const onSubmit = form.handleSubmit((data) => {
    const value =
      data.discountType === "fixed"
        ? Math.round(data.discountValue * 100)
        : Math.round(data.discountValue);

    const validFrom = data.validFrom
      ? new Date(data.validFrom + "T00:00:00Z").toISOString()
      : null;
    const validUntil = data.validUntil
      ? new Date(data.validUntil + "T23:59:59Z").toISOString()
      : null;

    const payload = {
      title: data.title,
      description: data.description || null,
      discountType: data.discountType,
      discountValue: value,
      badgeLabel: data.badgeLabel ? data.badgeLabel : null,
      badgeVariant: data.badgeVariant,
      stackableWithCoupons: data.stackableWithCoupons,
      validFrom,
      validUntil,
      isActive: data.isActive,
      treatmentIds: data.treatmentIds,
    };

    if (isEdit && offer) {
      updateMutation.mutate({ id: offer.id, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  const toggleTreatment = (id: string) => {
    const current = form.getValues("treatmentIds");
    if (current.includes(id)) {
      form.setValue(
        "treatmentIds",
        current.filter((t) => t !== id),
        { shouldValidate: true },
      );
    } else {
      form.setValue("treatmentIds", [...current, id], { shouldValidate: true });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edytuj ofertę" : "Nowa oferta"}</DialogTitle>
        </DialogHeader>

        <DialogForm onSubmit={onSubmit}>
          <DialogBody className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="o-title">Tytuł</Label>
              <Input id="o-title" {...form.register("title")} />
              {form.formState.errors.title && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.title.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="o-desc">Opis (opcjonalny)</Label>
              <Textarea id="o-desc" rows={2} {...form.register("description")} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="o-type">Typ</Label>
                <select
                  id="o-type"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                  {...form.register("discountType")}
                >
                  <option value="percentage">Zniżka procentowa</option>
                  <option value="fixed">Zniżka kwotowa</option>
                  <option value="badge_only">Tylko wyróżnienie</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="o-value">
                  Wartość{" "}
                  {watchType === "percentage"
                    ? "(%)"
                    : watchType === "fixed"
                      ? "(zł)"
                      : "(nieużywana)"}
                </Label>
                <Input
                  id="o-value"
                  type="number"
                  step={watchType === "fixed" ? "0.01" : "1"}
                  min="0"
                  disabled={watchType === "badge_only"}
                  {...form.register("discountValue")}
                />
              </div>
            </div>

            <div className="rounded-md border p-3 space-y-3 bg-muted/30">
              <div className="flex items-center justify-between">
                <Label>Etykieta (badge)</Label>
                <Badge variant={watchBadgeVariant === "danger" ? "destructive" : "default"}>
                  {computedBadgePreview}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="o-badge-label" className="text-xs">
                    Etykieta (pusta = auto)
                  </Label>
                  <Input
                    id="o-badge-label"
                    placeholder="np. NOWE"
                    maxLength={32}
                    {...form.register("badgeLabel")}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="o-badge-variant" className="text-xs">
                    Wariant koloru
                  </Label>
                  <select
                    id="o-badge-variant"
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                    {...form.register("badgeVariant")}
                  >
                    <option value="danger">Czerwony (zniżka)</option>
                    <option value="success">Zielony</option>
                    <option value="warning">Pomarańczowy</option>
                    <option value="info">Niebieski</option>
                    <option value="tonal">Stonowany</option>
                    <option value="default">Domyślny</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="o-from">Ważna od</Label>
                <Input
                  id="o-from"
                  type="date"
                  {...form.register("validFrom")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="o-until">Ważna do</Label>
                <Input
                  id="o-until"
                  type="date"
                  {...form.register("validUntil")}
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                id="o-stack"
                checked={watchStackable}
                onCheckedChange={(v) => form.setValue("stackableWithCoupons", v)}
              />
              <Label htmlFor="o-stack">Można łączyć z kuponami</Label>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                id="o-active"
                checked={watchActive}
                onCheckedChange={(v) => form.setValue("isActive", v)}
              />
              <Label htmlFor="o-active">{watchActive ? "Aktywna" : "Nieaktywna"}</Label>
            </div>

            <div className="space-y-2">
              <Label>Zabiegi objęte ofertą</Label>
              <p className="text-xs text-muted-foreground">
                Wybrane zabiegi: {watchTreatmentIds.length}
              </p>
              <TreatmentPicker
                mode="multi"
                selectedIds={watchTreatmentIds}
                onPick={toggleTreatment}
                enabled={open}
                listClassName="max-h-48"
                emptyText="Brak zabiegów. Najpierw dodaj zabiegi."
              />
              {form.formState.errors.treatmentIds && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.treatmentIds.message}
                </p>
              )}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
            >
              Anuluj
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending
                ? "Zapisywanie..."
                : isEdit
                  ? "Zapisz zmiany"
                  : "Dodaj ofertę"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
